'use client'

import * as React from 'react'
import { useParams } from 'next/navigation'

import { Box, Container, Divider, Grid, Link as MuiLink, Stack, styled, Typography } from '@mui/material'
import { signIn } from 'next-auth/react'

import { BreadcrumbsCustom } from '@/components/BreadcrumbsCustom/BreadcrumbsCustom'
import { ContactButton } from '@/components/ContactButton/ContactButton'
import { AddToFavorites } from '@/components/Favorite/FavoriteListActions/AddToFavorites'
import { ProductCard } from '@/components/ProductCard/ProductCard'
import { ProductCarousel } from '@/components/ProductCarousel/ProductCarousel'
import { ProductDetailsAccordion } from '@/components/ProductDetailsAccordion/ProductDetailsAccordion'
import { ProductDetailsBuyButton } from '@/components/ProductDetailsBuyButton'
import { ProductDetailsCarousel } from '@/components/ProductDetailsCarousel/ProductDetailsCarousel'
import { ProductDetailsSpecifications } from '@/components/ProductDetailsSpecificationsList/ProductDetailsSpecifications'
import { ProductDocuments } from '@/components/ProductDocuments/ProductDocuments'
import { ProductLabels } from '@/components/ProductPrice/ProductLabels'
import { ProductMinSaveLabels } from '@/components/ProductPrice/ProductMinSaveLabels'
import { ProductPrice } from '@/components/ProductPrice/ProductPrice'
import { PriceProductDetailsStack } from '@/components/ProductPrice/ProductPrice.styled'
import { ProductPartStatusAndStock } from '@/components/ProductStock/ProductPartStatusAndStock'
import { SanityComponent } from '@/components/SanityComponent/SanityComponent'
import { SignUpButton } from '@/components/SignUpButton/SignUpButton'
import { getPriceMarkers } from '@/helpers/getPriceMarkers'
import { getWebShopStatusVisibility } from '@/helpers/getWebShopStatusVisibility'
import { useProductPrice } from '@/hooks/bff/prices/getPrices/useProductPrice'
import { useActionsOfFavoriteList } from '@/hooks/useActionsOfFavoriteList'
import { useAddToCartState } from '@/hooks/useAddToCartState'
import { useCart } from '@/hooks/useCart'
import { useCartQuantityChange } from '@/hooks/useCartQuantityChange'
import { useHeaderContext } from '@/hooks/useHeaderContext'
import { useIsLoggedIn } from '@/hooks/useIsLoggedIn'
import { useMedia } from '@/hooks/useMedia'
import { usePackagingDescription } from '@/hooks/usePackagingDescription'
import { useProductViewTracking } from '@/hooks/useProductViewTracking'
import { useQuickSearchReset } from '@/hooks/useQuickSearchReset'
import { useSalesUnitDescription } from '@/hooks/useSalesUnitDescription'
import { useTenant } from '@/hooks/useTenant'
import { useTranslations } from '@/hooks/useTranslations'
import { ProductReferencesType } from '@/interfaces/product.interface'
import { getProductPageItemList, PRODUCT_PAGE_ITEM_INDEX } from '@/lib/eventBus/selectedItemList'

import type { ProductProps } from '@/interfaces/product.interface'
import type { ListContext } from '@/lib/eventBus/trackingEvents'
import type { UnpackedSanityProduct } from '@/lib/queries/sanityQueryStrings'

const ListDivider = styled(Divider)(({ theme }) => ({ borderColor: theme.palette.text.primary, marginBlock: 5 }))

const hasDocuments = (product: ProductProps) =>
  Boolean(
    product.productDatasheet ||
      product.technicalDatasheet ||
      product.safetyDatasheet ||
      product.brochures?.length ||
      product.userManuals?.length ||
      product.certificates?.length
  )

const getReferencedProductCodes = (product: ProductProps, referenceType: ProductReferencesType) =>
  product.productReferences
    ?.filter((reference) => reference.referenceType === referenceType)
    .map((reference) => reference.target?.code)
    .filter((code): code is string => !!code)

interface Props {
  product: ProductProps
  sanityProductData?: UnpackedSanityProduct | null
}

interface ProductClientMediaProps {
  isMobile: boolean
  product: ProductProps
  productCode: string
  labelText: string | null
  labelStyleClass: string | null
  favoriteListsIds: string[]
}

const ProductClientMedia: React.FC<ProductClientMediaProps> = ({
  isMobile,
  product,
  productCode,
  labelText,
  labelStyleClass,
  favoriteListsIds,
}) => {
  if (isMobile) {
    return (
      <ProductCard
        sx={{ maxWidth: 'unset', margin: 0, maxHeight: 'unset', width: '100%', display: { xs: 'flex', md: 'none' } }}
        productCode={product.code ?? productCode}
        product={product}
        favoriteListsIds={favoriteListsIds}
      />
    )
  }

  return (
    <>
      <ProductDetailsCarousel product={product} />
      <ProductLabels className="priceLabelDetails" labelText={labelText} colorClassName={labelStyleClass} />
    </>
  )
}

interface ProductClientHeaderInfoProps {
  product: ProductProps
  translate: ReturnType<typeof useTranslations>['translate']
  e6ProductId: string
  salesUnitDescription: string
  packagingDescription: string
}

const ProductClientHeaderInfo: React.FC<ProductClientHeaderInfoProps> = ({
  product,
  translate,
  e6ProductId,
  salesUnitDescription,
  packagingDescription,
}) => (
  <>
    <Typography variant="pageHeading3" data-testid="productDetails-name">
      {product.name}
    </Typography>
    <Box sx={{ my: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="description">{`${translate('productDetails.productArtNo')} ${
        product.code
      } ${e6ProductId}`}</Typography>

      <Box display="inline-flex" flexWrap="wrap" gap={1}>
        {!!product.size && (
          <>
            <Typography variant="body1">{`${translate('productDetails.productSize')}: ${product.size}`}</Typography>
            <ListDivider orientation="vertical" variant="fullWidth" flexItem />
          </>
        )}
        {!!salesUnitDescription && (
          <>
            <Typography variant="body1">{`${translate(
              'productDetails.productSalesUnit'
            )}: ${salesUnitDescription}`}</Typography>
            <ListDivider orientation="vertical" variant="fullWidth" flexItem />
          </>
        )}
        {!!packagingDescription && (
          <Typography variant="body1">{`${translate(
            'product.packagingDescription'
          )}: ${packagingDescription}`}</Typography>
        )}
      </Box>
      <ProductPartStatusAndStock product={product} />
    </Box>
  </>
)

interface ProductClientPriceActionsProps {
  product: ProductProps
  quantity: number
  priceData: ReturnType<typeof useProductPrice>['priceData']
  minSaveLabel: string | null
  shouldShowPrice: boolean
  shouldShowBuyButton: boolean
  shouldShowContactButton: boolean
  shouldShowSignUpButton: boolean
  isLoggedIn: boolean
  isLoadingCart: boolean
  isDisabled: boolean
  allowedQuantity: number
  handleAddToCart: VoidFunction
  handleUpdateCounter: (value: number) => void
  favoriteListsIds: string[]
}

const ProductClientPriceActions: React.FC<ProductClientPriceActionsProps> = ({
  product,
  quantity,
  priceData,
  minSaveLabel,
  shouldShowPrice,
  shouldShowBuyButton,
  shouldShowContactButton,
  shouldShowSignUpButton,
  isLoggedIn,
  isLoadingCart,
  isDisabled,
  allowedQuantity,
  handleAddToCart,
  handleUpdateCounter,
  favoriteListsIds,
}) => {
  if (product.displayOnly) {
    return null
  }

  return (
    <PriceProductDetailsStack sx={{ marginBlock: 3 }} gap={1}>
      {minSaveLabel && (
        <ProductMinSaveLabels
          minSaveLabel={minSaveLabel}
          minimumQuantity={priceData?.minimumQuantity ?? 1}
          percentage={priceData?.discountPercentage}
        />
      )}
      <Stack flexDirection="row" justifyContent="space-between" alignItems="flex-end">
        {shouldShowPrice && (
          <ProductPrice product={product} priceProductDetails quantity={quantity} data-testid="productDetails-price" />
        )}
        <Stack maxWidth="270px" flexDirection="row" gap={1} alignItems="center">
          {shouldShowBuyButton && (
            <ProductDetailsBuyButton
              allowedQuantity={allowedQuantity}
              isLoading={isLoadingCart}
              isDisabled={isDisabled}
              onClick={handleAddToCart}
              quantity={quantity}
              product={product}
              handleUpdateCounter={handleUpdateCounter}
            />
          )}
          {shouldShowContactButton && <ContactButton />}
          {shouldShowSignUpButton && <SignUpButton />}
          {isLoggedIn && (
            <AddToFavorites productCode={product.code ?? ''} isInFavoriteList={favoriteListsIds.length > 0} />
          )}
        </Stack>
      </Stack>
    </PriceProductDetailsStack>
  )
}

interface ProductClientDocumentsAccordionProps {
  translate: ReturnType<typeof useTranslations>['translate']
  product: ProductProps
}

const ProductClientDocumentsAccordion: React.FC<ProductClientDocumentsAccordionProps> = ({ translate, product }) => {
  if (!hasDocuments(product)) {
    return null
  }

  const {
    productDatasheet: productDataSheet,
    technicalDatasheet: technicalDataSheet,
    safetyDatasheet: safetyDataSheet,
    brochures,
    userManuals,
    certificates,
  } = product

  return (
    <ProductDetailsAccordion elementId="panel3d" title={translate('productDetails.documents')}>
      <Box sx={{ paddingTop: 1 }}>
        {productDataSheet && (
          <ProductDocuments
            name={translate('productDetails.productDataSheet')?.toString()}
            productDocument={productDataSheet}
          />
        )}
        {technicalDataSheet && (
          <ProductDocuments
            name={translate('productDetails.technicalDataSheet')?.toString()}
            productDocument={technicalDataSheet}
          />
        )}
        {safetyDataSheet && (
          <ProductDocuments
            name={translate('productDetails.safetyDataSheet')?.toString()}
            productDocument={safetyDataSheet}
          />
        )}
        {!!brochures?.length && (
          <ProductDocuments name={translate('productDetails.brochures')?.toString()} productDocument={brochures} />
        )}
        {!!userManuals?.length && (
          <ProductDocuments name={translate('productDetails.userManual')?.toString()} productDocument={userManuals} />
        )}
        {!!certificates?.length && (
          <ProductDocuments
            name={translate('productDetails.certificates')?.toString()}
            productDocument={certificates}
          />
        )}
      </Box>
    </ProductDetailsAccordion>
  )
}

interface ReferencedProductsCarouselProps {
  title: React.ReactNode
  productCodes?: string[]
  listContext: ListContext
}

const ReferencedProductsCarousel: React.FC<ReferencedProductsCarouselProps> = ({
  title,
  productCodes,
  listContext,
}) => {
  if (!productCodes?.length) {
    return null
  }

  return (
    <Container disableGutters>
      <Typography variant="pageHeading1">{title}</Typography>
      <ProductCarousel
        productsIds={productCodes}
        listContext={listContext}
        numberOfProductsToShow={productCodes.length}
      />
    </Container>
  )
}

interface ProductClientRelatedProductsProps {
  translate: ReturnType<typeof useTranslations>['translate']
  content: UnpackedSanityProduct['content'] | undefined
  hasProductReferences: boolean
  similarProducts?: string[]
  accessoriesProducts?: string[]
  upsellingProducts?: string[]
}

const ProductClientRelatedProducts: React.FC<ProductClientRelatedProductsProps> = ({
  translate,
  content,
  hasProductReferences,
  similarProducts,
  accessoriesProducts,
  upsellingProducts,
}) => (
  <Grid container direction="column" gap={5} mt={5}>
    {content
      ?.filter((item) => !!item._type)
      .map((item) => (
        <Grid item xs={12} pr={{ xs: 0, lg: 3 }} key={item._id}>
          <SanityComponent sanityComponent={item} />
        </Grid>
      ))}

    {hasProductReferences && (
      <>
        <ReferencedProductsCarousel
          title={translate('product.similarProducts')}
          productCodes={similarProducts}
          listContext={{ kind: 'relatedProducts' }}
        />
        <ReferencedProductsCarousel
          title={translate('product.accessoriesProducts')}
          productCodes={accessoriesProducts}
          listContext={{ kind: 'accessoryProducts' }}
        />
        <ReferencedProductsCarousel
          title={translate('product.upsellingProducts')}
          productCodes={upsellingProducts}
          listContext={{ kind: 'upsellingProducts' }}
        />
      </>
    )}
  </Grid>
)

export const ProductClient: React.FC<Props> = ({ product, sanityProductData }) => {
  const { isMobile } = useMedia()
  const { translate } = useTranslations()
  const tenant = useTenant()
  const cartOperations = useCart()
  const { cart, isLoadingCart } = cartOperations
  const { addToCart } = useCartQuantityChange(cartOperations)
  const [quantity, setQuantity] = React.useState(1)
  const params = useParams<{ productCode: string }>()
  const isLoggedIn = useIsLoggedIn()
  const { reset } = useQuickSearchReset()
  const { isSearchOpen, setIsSearchOpen } = useHeaderContext()
  const { getListOfFavoriteList } = useActionsOfFavoriteList(product.code ? [product.code] : [])

  const { shouldShowBuyButton, shouldShowContactButton, shouldShowPrice, shouldShowSignUpButton } =
    getWebShopStatusVisibility(product.productWebshopStatus ?? null, product.productPartStatus ?? null, isLoggedIn)
  const salesUnitDescription = useSalesUnitDescription(product)
  const packagingDescription = usePackagingDescription(product)

  const productCode = String(params.productCode)

  const { allowedQuantity, isDisabled } = useAddToCartState({
    productCode,
    quantity,
    productStock: product.productStock,
    productPartStatus: product.productPartStatus,
  })

  const handleAddToCart = React.useCallback(async () => {
    try {
      await addToCart({
        product,
        productCode,
        quantity,
        listContext: getProductPageItemList(product.code),
        index: PRODUCT_PAGE_ITEM_INDEX,
      })
    } catch {
      /* empty */
    }
  }, [addToCart, product, productCode, quantity])

  // Close search if the product changes & sync quantity from cart
  React.useEffect(() => {
    if (isSearchOpen) {
      setIsSearchOpen(false)
      reset()
    }
    const cartProduct = cart?.entries?.find((entry) => entry.product?.code === productCode)
    setQuantity(cartProduct?.quantity || 1)
  }, [productCode, cart?.entries, isSearchOpen, setIsSearchOpen, reset])

  const { priceData } = useProductPrice(product.code ?? productCode, quantity)
  useProductViewTracking(product, shouldShowPrice)

  const categoryId = product.categories?.[0]?.code ?? ''
  const e6ProductId = product.e6ProductId ? `(${translate('productDetails.e6ProductId')} ${product.e6ProductId})` : ''
  const similarProducts = React.useMemo(
    () => getReferencedProductCodes(product, ProductReferencesType.similar),
    [product]
  )
  const upsellingProducts = React.useMemo(
    () => getReferencedProductCodes(product, ProductReferencesType.upselling),
    [product]
  )
  const accessoriesProducts = React.useMemo(
    () => getReferencedProductCodes(product, ProductReferencesType.accessories),
    [product]
  )

  const { labelStyleClass, labelText, minSaveLabel } = getPriceMarkers(priceData, tenant.currency, tenant.locale)
  const favoriteListsIds = getListOfFavoriteList(product.code)

  return (
    <main className="flex-1">
      <Container sx={{ my: 2 }}>
        <BreadcrumbsCustom categoryId={categoryId} title={product.name} />
      </Container>
      <Container sx={{ mb: 4, mt: { xs: 0, md: 4 } }}>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item sx={{ p: 2, pr: 0 }} xs={12} md={6}>
            <Box
              sx={(theme) => ({
                height: isMobile ? 'auto' : theme.customDimensions.productDetailsImageHeight,
                position: 'relative',
                wordBreak: 'break-all',
                '.MuiBox-root.priceLabelDetails': {
                  position: 'absolute',
                  top: 14,
                  left: 0,
                },
              })}
            >
              <ProductClientMedia
                isMobile={isMobile}
                product={product}
                productCode={productCode}
                labelText={labelText}
                labelStyleClass={labelStyleClass}
                favoriteListsIds={favoriteListsIds}
              />
            </Box>
          </Grid>

          <Grid item sx={{ p: 2 }} xs={12} md={6}>
            {!isMobile && (
              <>
                <ProductClientHeaderInfo
                  product={product}
                  translate={translate}
                  e6ProductId={e6ProductId}
                  salesUnitDescription={salesUnitDescription}
                  packagingDescription={packagingDescription}
                />
                <ProductClientPriceActions
                  product={product}
                  quantity={quantity}
                  priceData={priceData}
                  minSaveLabel={minSaveLabel}
                  shouldShowPrice={shouldShowPrice}
                  shouldShowBuyButton={shouldShowBuyButton}
                  shouldShowContactButton={shouldShowContactButton}
                  shouldShowSignUpButton={shouldShowSignUpButton}
                  isLoggedIn={isLoggedIn}
                  isLoadingCart={isLoadingCart}
                  isDisabled={isDisabled}
                  allowedQuantity={allowedQuantity}
                  handleAddToCart={handleAddToCart}
                  handleUpdateCounter={setQuantity}
                  favoriteListsIds={favoriteListsIds}
                />
              </>
            )}

            {!isLoggedIn && (
              <Box sx={{ my: 1 }}>
                <MuiLink
                  variant="body2"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => signIn('SAP', undefined, { prompt: 'login' })}
                >
                  {translate('productDetails.logInLink')}
                </MuiLink>
              </Box>
            )}

            <ProductDetailsAccordion
              defaultExpanded
              elementId="panel1d"
              title={translate('productDetails.productDescription')}
            >
              <Typography
                variant="body1"
                component="div"
                dangerouslySetInnerHTML={{ __html: product.description ?? '' }}
              />
            </ProductDetailsAccordion>

            <ProductDetailsAccordion
              elementId="panel2d"
              title={translate('productDetails.productSpecifications')?.toString()}
            >
              <ProductDetailsSpecifications
                product={product}
                salesUnitDescription={salesUnitDescription}
                packagingDescription={packagingDescription}
              />
            </ProductDetailsAccordion>

            <ProductClientDocumentsAccordion translate={translate} product={product} />
          </Grid>
        </Grid>

        <ProductClientRelatedProducts
          translate={translate}
          content={sanityProductData?.content}
          hasProductReferences={Array.isArray(product.productReferences)}
          similarProducts={similarProducts}
          accessoriesProducts={accessoriesProducts}
          upsellingProducts={upsellingProducts}
        />
      </Container>
    </main>
  )
}
